import { useMemo } from 'react'
import { useRouter } from '@tanstack/react-router'
import { CircleCheckIcon } from 'lucide-react'
import { toast } from 'sonner'

import { useSmartForm } from '@/hooks/use-smart-form.tsx'
import { Button } from '@/components/ui/button.tsx'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx'
import { Input } from '@/components/ui/input.tsx'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import { AdaptiveTextarea } from '@/components/adaptive-textarea.tsx'
import { FormActions } from '@/components/form-actions.tsx'
import { PageTitle } from '@/components/layout/page-title.tsx'
import { SortableList } from '@/components/sortable-list.tsx'
import { LoadingSpinner } from '@/components/spinner/loading-spinner.tsx'
import { useCreateAttributeGroup } from '@/features/attribute-groups/hooks/use-create-attribute-group.ts'
import { useUpdateAttributeGroupById } from '@/features/attribute-groups/hooks/use-update-attribute-group.ts'
import { AttributeGroup } from '@/features/attribute-groups/types.ts'
import { useFindAttributes } from '@/features/attributes/hooks/use-find-attributes.ts'

interface AttributeGroupFormProps {
  attributeGroupId?: string
  attributeGroup?: AttributeGroup
  onSubmitted?: (attributeGroup: AttributeGroup) => void
}

type AttributeGroupFormData = {
  name?: AttributeGroup['name']
  attributeIds?: Array<string>
} & Pick<AttributeGroup, 'document_title_prompt' | 'document_summary_prompt'>

export function AttributeGroupForm(props: AttributeGroupFormProps) {
  const router = useRouter()
  const { attributeGroupId, attributeGroup, onSubmitted } = props

  const formData = useMemo(
    () => ({
      name: attributeGroup?.name,
      attributeIds: attributeGroup?.attributes_on_group?.map(
        (a) => a.attribute_id
      ),
      document_title_prompt: attributeGroup?.document_title_prompt ?? null,
      document_summary_prompt: attributeGroup?.document_summary_prompt ?? null,
    }),
    [
      attributeGroup?.attributes_on_group,
      attributeGroup?.document_summary_prompt,
      attributeGroup?.document_title_prompt,
      attributeGroup?.name,
    ]
  )
  const form = useSmartForm<AttributeGroupFormData>({
    values: formData,
  })

  const findAttributesQuery = useFindAttributes()
  const attributeOptions = useMemo(
    () =>
      findAttributesQuery?.data?.data?.map((a) => ({
        label: a.name,
        value: a.id,
      })),
    [findAttributesQuery?.data?.data]
  )

  const createAttributeGroupMutation = useCreateAttributeGroup()
  const updateAttributeGroupMutation = useUpdateAttributeGroupById()

  const onSubmit = async (data: AttributeGroupFormData) => {
    const attributesOnGroup =
      data?.attributeIds?.map((attributeId, index) => ({
        attribute_id: attributeId,
        order: index + 1,
      })) ?? []
    if (attributeGroupId) {
      const result = await updateAttributeGroupMutation.mutateAsync({
        attributeGroupId,
        data: {
          ...data,
          attributes_on_group: attributesOnGroup,
        },
      })

      toast('Gruppo Attributi salvato', { icon: <CircleCheckIcon /> })

      return onSubmitted?.(result?.data as AttributeGroup)
    }

    const result = await createAttributeGroupMutation.mutateAsync({
      name: data.name as string,
      attributes_on_group: attributesOnGroup,
    })

    toast('Gruppo Attributi creato', { icon: <CircleCheckIcon /> })
    await onSubmitted?.(result?.data as AttributeGroup)
  }

  return (
    <>
      <ScrollArea className="-mx-1 h-full p-4">
        <Form {...form}>
          <PageTitle>
            {attributeGroupId ? (
              <>Modifica Gruppo Attributi: {attributeGroup?.name}</>
            ) : (
              <>Nuovo Gruppo Attributi</>
            )}
          </PageTitle>
          <div className="max-w-screen-sm px-1">
            <form className="space-y-8" onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormDescription></FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="document_title_prompt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prompt Titolo</FormLabel>
                    <FormControl>
                      <AdaptiveTextarea
                        rows={1}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormDescription>
                      Opzionale: fornisci indicazioni su come generare il testo
                      nel campo 'Titolo' dei documenti.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="document_summary_prompt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prompt Descrizione</FormLabel>
                    <FormControl>
                      <AdaptiveTextarea
                        rows={1}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormDescription>
                      Opzionale: fornisci indicazioni su come generare il testo
                      nel campo 'Descrizione' dei documenti.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {findAttributesQuery.isPending && <LoadingSpinner />}

              {findAttributesQuery.isSuccess && (
                <FormField
                  control={form.control}
                  name="attributeIds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Elenco Attributi</FormLabel>
                      <FormControl>
                        <SortableList
                          value={field.value}
                          placeholder="Aggiungi nuovo"
                          options={attributeOptions}
                          onSelectionChange={(values) => field.onChange(values)}
                        />
                      </FormControl>
                      <FormDescription></FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormActions>
                <Button
                  type="reset"
                  variant="outline"
                  size="lg"
                  onClick={() =>
                    attributeGroupId ? form.reset() : router.history.back()
                  }
                >
                  {attributeGroupId ? 'Reset' : 'Annulla'}
                </Button>
                <Button
                  type="submit"
                  size="lg"
                  disabled={
                    form.formState.isSubmitting || !form.formState.isDirty
                  }
                >
                  Salva
                </Button>
              </FormActions>
            </form>
          </div>
        </Form>
      </ScrollArea>
    </>
  )
}
